/**
 * Calculates the Levenshtein distance between two strings.
 * The Levenshtein distance is the minimum number of single-character edits
 * (insertions, deletions or substitutions) required to change one string into the other.
 *
 * @param str1 - The first string
 * @param str2 - The second string
 * @returns The Levenshtein distance between the two strings
 *
 * @example
 * calculateLevenshteinDistance('kitten', 'sitting') // returns 3
 * calculateLevenshteinDistance('hello', 'hello') // returns 0
 */
export const calculateLevenshteinDistance = (str1: string, str2: string): number => {
	const len1 = str1.length;
	const len2 = str2.length;

	// Create a 2D array for dynamic programming
	const dp: number[][] = Array(len1 + 1)
		.fill(null)
		.map(() => Array(len2 + 1).fill(0));

	// Initialize base cases
	for (let i = 0; i <= len1; i++) {
		dp[i][0] = i;
	}
	for (let j = 0; j <= len2; j++) {
		dp[0][j] = j;
	}

	// Fill in the rest of the matrix
	for (let i = 1; i <= len1; i++) {
		for (let j = 1; j <= len2; j++) {
			const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;

			dp[i][j] = Math.min(
				dp[i - 1][j] + 1, // deletion
				dp[i][j - 1] + 1, // insertion
				dp[i - 1][j - 1] + cost, // substitution
			);
		}
	}

	return dp[len1][len2];
};

/**
 * Calculates the edit rate as a percentage.
 * This represents how much the text was modified relative to its length.
 *
 * @param originalText - The original text (selected suggestion)
 * @param editedText - The edited text (final submitted query)
 * @returns An object containing the edit distance, edit rate (0-100), and the max length used for calculation
 *
 * @example
 * calculateEditRate('hello world', 'hello there') // returns { distance: 5, editRate: 45.45, maxLength: 11 }
 * calculateEditRate('hello', 'hello') // returns { distance: 0, editRate: 0, maxLength: 5 }
 */
export const calculateEditRate = (
	originalText: string,
	editedText: string,
): {
	distance: number;
	editRate: number;
	maxLength: number;
} => {
	const distance = calculateLevenshteinDistance(originalText, editedText);
	const maxLength = Math.max(originalText.length, editedText.length);

	// Avoid division by zero
	if (maxLength === 0) {
		return { distance: 0, editRate: 0, maxLength: 0 };
	}

	// Calculate edit rate as a percentage
	const editRate = (distance / maxLength) * 100;

	return {
		distance,
		editRate: Math.round(editRate * 100) / 100, // Round to 2 decimal places
		maxLength,
	};
};

export interface EditDistanceMetrics {
	editDistance: number;
	maxNormalizedEditRate: number; // Percentage (0-100), normalized by max string length
}

/**
 * Calculates edit distances for multiple selected texts against a final query.
 *
 * @param selectedTexts - Array of texts that were selected during the session
 * @param submittedQuery - The final query text that was submitted
 * @returns Array of edit distance metrics, one for each selected text
 *
 * @example
 * calculateEditDistances(['hello', 'hello world'], 'hello world!')
 * // returns [{ editDistance: 7, editRate: 58.33 }, { editDistance: 1, editRate: 8.33 }]
 */
export const calculateEditDistances = (
	selectedTexts: string[],
	submittedQuery: string,
): EditDistanceMetrics[] => {
	return selectedTexts.map((selectedText) => {
		const { distance, editRate } = calculateEditRate(selectedText, submittedQuery);
		return { editDistance: distance, maxNormalizedEditRate: editRate };
	});
};
